import React, { Component } from 'react';
import PropTypes from 'prop-types';
import octicons from 'octicons';
import Textarea from 'react-autosize-textarea';
import { Picker } from 'emoji-mart';
import 'emoji-mart/css/emoji-mart.css';

import Markdown from '../markdown';
import MarkdownToolbar from '../markdown_toolbar';

export default class MarkdownInput extends Component {
  static propTypes = {
    className: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func,
    onClickOutside: PropTypes.func,
    onCustomBlur: PropTypes.func,
    innerRef: PropTypes.object,
    renderMarkdown: PropTypes.func,

    // Custom props that are passed to the children
    // Regular textarea attributes are also supported
    rows: PropTypes.number, // eslint-disable-line react/no-unused-prop-types
    maxRows: PropTypes.number, // eslint-disable-line react/no-unused-prop-types
    style: PropTypes.object,
    toolbarAlwaysVisible: PropTypes.bool,
    toolbarAlwaysInvisible: PropTypes.bool,
    wrapperClassName: PropTypes.string,
    isComment: PropTypes.bool,
    children: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.node),
      PropTypes.node,
    ]),
  }

  static defaultProps = {
    className: '',
    value: '',
    onChange() {},
    onClickOutside() {},
    onCustomBlur() {},
    innerRef: null,
    rows: 7,
    maxRows: 14, // The user can still expand the textarea more
    style: {},
    toolbarAlwaysVisible: false,
    toolbarAlwaysInvisible: false,
    wrapperClassName: '',
    isComment: false,
    children: null,
    renderMarkdown: value => Markdown.render(value),
  }

  constructor(props) {
    super(props);
    this.textarea = this.props.innerRef || React.createRef();
    this.state = {
      writing: true,
      focused: false,
      showPicker: false,
      value: this.toSafeValue(this.props.value),
    };
  }

  // Prevent issue when using values submitted from Windows. For eg, selecting "World"
  // in "Hello\r\nWorld" and applying bold would result in "Hell**o\r\nWorl**d".
  // It also prevents a null value which would raise a react warning.
  toSafeValue(value) {
    return (value || '').replace(/\r\n/g, '\n');
  }


  enableWrite = () => {
    this.setState({ writing: true });
  }

  enablePreview = () => {
    this.setState({ writing: false });
  }

  handleChange = (e) => {
    this.setState({ value: e.target.value });
  }

  handleFocus = () => {
    this.setState({ focused: true });
  }

  handleClickOutside = (e) => {
    if (this.state.focused && !this.node.contains(e.target)) {
      this.setState({ writing: true, focused: false, showPicker: false });
      this.props.onClickOutside(e);
      this.props.onCustomBlur(this.state.value);
    } else if (this.state.showPicker && !this.picker.contains(e.target)) {
      this.setState({ showPicker: false });
      this.props.onClickOutside(e);
    }
  }

  // To be called before updating state with a new value, this method
  // improves undo/redo history in all browsers.
  insertValue(value) {
    this.textarea.current.focus();
    this.textarea.current.setSelectionRange(0, this.textarea.current.value.length);

    // Firefox allows us to use `setState` normally without breaking history
    // In Chrome and others it's better to use this in order for undo/redo to behave correctly
    // Unfortunately this will call `this.props.onChange` once per line
    if (!/firefox/i.test(navigator.userAgent)) {
      document.execCommand('insertText', false, value);
    }
  }

  actionClick = (e) => {
    if (!this.state.writing) return;

    const buttonData = $(e.currentTarget).data();
    let selectionStart = this.textarea.current.selectionStart;
    let selectionEnd = this.textarea.current.selectionEnd;
    let value = this.state.value;

    const mtc = new MarkdownToolbar();
    value = mtc.render(buttonData, selectionStart, selectionEnd, value);
    selectionStart = mtc.selectionStart;
    selectionEnd = mtc.selectionEnd;

    this.insertValue(value);
    this.setState({ value }, () => {
      this.textarea.current.setSelectionRange(selectionStart, selectionEnd);
    });
  }

  pickerClick = () => {
    this.setState({ showPicker: true });
  }

  pickEmoji = (emoji) => {
    const cursorPos = this.textarea.current.selectionEnd;
    let value = this.state.value;
    value = value.substr(0, cursorPos) + emoji.native + value.substr(cursorPos);

    this.insertValue(value);
    this.setState({ value, showPicker: false }, () => {
      this.textarea.current.setSelectionRange(cursorPos, cursorPos);
      this.textarea.current.focus();
    });
  }

  componentDidMount() {
    if (!this.props.toolbarAlwaysVisible) {
      $(document).on('click', this.handleClickOutside);
    }
  }

  componentWillUnmount() {
    if (!this.props.toolbarAlwaysVisible) {
      $(document).off('click', this.handleClickOutside);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.value !== this.state.value) {
      this.props.onChange(this.state.value);
    }
  }

  componentWillReceiveProps(nextProps) {
    const value = this.toSafeValue(nextProps.value);

    // In case the value received is the same as the initial value, we will not do anything.
    // Necessary in case other components cause Turbolinks to rerender components in the page.
    const initialValue = this.toSafeValue(this.props.value);

    if (this.state.value !== value && initialValue !== value) {
      this.insertValue(value);
      this.setState({ value });
    }
  }

  renderToolbar() {
    return (
      <div className="MarkdownInput-toolbar u-rounded-t">
        <button type="button" className={`MarkdownInput-action ${this.state.writing ? 'is-active' : ''}`}
          onClick={this.enableWrite}>
          Write
        </button>
        <button type="button" className={`MarkdownInput-action ${this.state.writing ? '' : 'is-active'}`}
          onClick={this.enablePreview}>
          Preview
        </button>

        <button type="button" className="MarkdownInput-action u-ml-4" onClick={this.actionClick}
          data-prefix="**" data-suffix="**" data-hotkey="b">
          { this.renderIcon('bold') }
        </button>
        <button type="button" className="MarkdownInput-action" onClick={this.actionClick}
          data-prefix="_" data-suffix="_" data-hotkey="i">
          { this.renderIcon('italic') }
        </button>
        <button type="button" className="MarkdownInput-action" onClick={this.actionClick}
          data-prefix="> " data-multiline="true">
          { this.renderIcon('quote') }
        </button>
        <button type="button" className="MarkdownInput-action" onClick={this.actionClick}
          data-prefix="[" data-suffix="](url)" data-hotkey="k">
          { this.renderIcon('link') }
        </button>
        <button type="button" className="MarkdownInput-action" onClick={this.actionClick}
          data-prefix="- " data-multiline="true">
          { this.renderIcon('list-unordered') }
        </button>
        <button type="button" className="MarkdownInput-action" onClick={this.actionClick}
          data-prefix="1. " data-multiline="true">
          { this.renderIcon('list-ordered') }
        </button>
        <button type="button" className="MarkdownInput-action" onClick={this.pickerClick}>
          { this.renderIcon('smiley') }
        </button>

        <a className="MarkdownInput-help" href="http://commonmark.org/help/">
          { this.renderIcon('markdown', { class: 'MarkdownInput-help-icon' })
          } Markdown styling is supported
        </a>
      </div>
    );
  }

  renderIcon(name, options = { class: 'MarkdownInput-icon' }) {
    return <span dangerouslySetInnerHTML={{ __html: octicons[name].toSVG(options) }} />;
  }

  // The reason why we set `display:none` in the textarea instead of not rendering is so that
  // if the user as resized it, it will keep the same height after the preview is toggled
  render() {
    const { onClickOutside, onCustomBlur, innerRef, renderMarkdown, toolbarAlwaysVisible, toolbarAlwaysInvisible, isComment, wrapperClassName, ...textareaProps } = this.props;
    const minHeight = this.textarea.current ? $(this.textarea.current).outerHeight() : null;
    const isActive = toolbarAlwaysVisible || this.state.focused;

    return (
      <div
        className={`MarkdownInput ${isActive ? 'is-focused' : ''} ${isActive && !toolbarAlwaysInvisible ? 'has-toolbar-visible' : ''} ${isComment ? 'is-comment' : ''} ${wrapperClassName}`}
        ref={(node) => { this.node = node; }}
      >
        { isActive && !toolbarAlwaysInvisible && this.renderToolbar() }
        { this.state.showPicker &&
          <div ref={(picker) => { this.picker = picker; }}>
            <Picker
              emoji=""
              title=""
              native
              onClick={this.pickEmoji}
              emojiSize={20}
              perLine={12}
              color="#2d84bd"
            />
          </div>
        }

        <Textarea {...textareaProps}
          ref={this.textarea}
          className={`MarkdownInput-write ${this.props.className}`}
          style={Object.assign({ display: this.state.writing ? 'block' : 'none' }, this.props.style)}
          onFocus={this.handleFocus}
          onChange={this.handleChange}
          value={this.state.value}
        />

        { !this.state.writing &&
          <div className="MarkdownInput-preview Readability"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(this.state.value) }}
            style={{ minHeight }}
          />
        }

        { this.props.children }
      </div>
    );
  }
}
